/**
 * Profile page card: the people you blocked and muted, with Unblock / Unmute.
 */
import React, { useEffect, useMemo, useState } from 'react';
import { Ban, MicOff } from 'lucide-react';
import { useSafetyStore } from '../../stores/safetyStore';
import { fetchNames, unblock, unmute } from '../../services/safety';
import './safety.css';

export default function BlockedUsersCard() {
  const { blocked, muted, names: known, loaded } = useSafetyStore();
  const blockedIds = useMemo(() => Object.keys(blocked).filter((id) => !id.startsWith('k:')), [blocked]);
  const mutedIds = useMemo(() => Object.keys(muted).filter((id) => !id.startsWith('k:') && !blocked[id]), [muted, blocked]);
  const [names, setNames] = useState<Record<string, string>>({});

  useEffect(() => {
    const missing = [...blockedIds, ...mutedIds].filter((id) => !names[id]);
    if (!missing.length) return;
    let alive = true;
    fetchNames(missing).then((n) => { if (alive) setNames((prev) => ({ ...prev, ...n })); });
    return () => { alive = false; };
  }, [blockedIds, mutedIds]); // eslint-disable-line react-hooks/exhaustive-deps

  const nameOf = (id: string) => names[id] || known[id] || 'Player';

  const Row = ({ id, kind }: { id: string; kind: 'block' | 'mute' }) => (
    <li className="gx-safety-prow">
      <span className="gx-safety-prow__name">{nameOf(id)}</span>
      <button className="gx-safety-btn" onClick={() => void (kind === 'block' ? unblock(id) : unmute(id))}>
        {kind === 'block' ? 'Unblock' : 'Unmute'}
      </button>
    </li>
  );

  return (
    <div className="profile-card gx-safety-profile" data-safety="blocked-users">
      <div className="profile-card-header">
        <Ban size={30} className="profile-icon" />
        <div>
          <h2>Blocked users</h2>
          <p className="profile-email">You don't see or hear each other in worlds, and they can't invite you or knock on your worlds.</p>
        </div>
      </div>
      {!loaded ? <p className="gx-safety-muted">Loading…</p> : blockedIds.length === 0
        ? <p className="gx-safety-muted">You haven't blocked anyone. To block someone, click their avatar or name in a world.</p>
        : <ul className="gx-safety-plist">{blockedIds.map((id) => <Row key={id} id={id} kind="block" />)}</ul>}

      {mutedIds.length > 0 && <>
        <h3 className="gx-safety-psub"><MicOff size={15} /> Muted</h3>
        <ul className="gx-safety-plist">{mutedIds.map((id) => <Row key={id} id={id} kind="mute" />)}</ul>
      </>}
    </div>
  );
}
